{% block uc_free_product_manager %}
<div class="uc-free-product-manager">

    {% block uc_free_product_manager_toolbar %}
    <div class="uc-free-product-manager__toolbar">
        <sw-button v-if="acl.can('sellops_ultimate_cart.editor')" variant="primary" size="small" @click="openCreate">
            {{ $tc('sellops-ultimate-cart.freeProducts.addButton') }}
        </sw-button>
    </div>
    {% endblock %}

    {% block uc_free_product_manager_list %}
    <sw-loader v-if="isLoading" />

    {# Load error — distinct from "no rules yet" so a failed request never
       reads as "your free-product rules were deleted" #}
    <sw-alert v-else-if="loadError" variant="error">
        <strong>{{ $tc('sellops-ultimate-cart.freeProducts.loadErrorTitle') }}</strong>
        <p>{{ $tc('sellops-ultimate-cart.freeProducts.loadErrorSubline') }}</p>
    </sw-alert>

    <p v-else-if="!sortedRules.length" class="uc-free-product-manager__empty">
        {{ $tc('sellops-ultimate-cart.freeProducts.emptyState') }}
    </p>

    <table v-else class="uc-free-product-manager__table">
        <thead>
            <tr>
                <th>{{ $tc('sellops-ultimate-cart.freeProducts.columnProduct') }}</th>
                <th>{{ $tc('sellops-ultimate-cart.freeProducts.columnGroup') }}</th>
                <th>{{ $tc('sellops-ultimate-cart.freeProducts.columnCartValue') }}</th>
                <th>{{ $tc('sellops-ultimate-cart.freeProducts.columnValidity') }}</th>
                <th>{{ $tc('sellops-ultimate-cart.freeProducts.columnPriority') }}</th>
                <th>{{ $tc('sellops-ultimate-cart.freeProducts.columnActive') }}</th>
                <th></th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="rule in sortedRules" :key="rule.id">
                <td class="uc-free-product-manager__product">
                    {{ rule.productName ?? rule.productId }}
                    <span v-if="rule.oncePerCustomer" class="uc-free-product-manager__chip">
                        {{ $tc('sellops-ultimate-cart.freeProducts.oncePerCustomerHint') }}
                    </span>
                    {# Compact indicator for the chosen celebration animation —
                       only shown once one is actually picked, mirrors the same
                       badge in uc-milestone-manager's table. #}
                    <span
                        v-if="rule.animationType && rule.animationType !== 'none'"
                        class="uc-free-product-manager__chip"
                        :title="$tc('sellops-ultimate-cart.freeProducts.animationType.' + rule.animationType)"
                    >
                        ✨ {{ $tc('sellops-ultimate-cart.freeProducts.animationType.' + rule.animationType) }}
                    </span>
                </td>
                <td>
                    <span v-if="rule.groupId" class="uc-free-product-manager__chip" :title="rule.groupId">
                        {{ groupLabel(rule) }}
                    </span>
                    <span v-else class="uc-free-product-manager__muted">—</span>
                </td>
                <td>{{ rule.cartValue }}</td>
                <td>{{ validityLabel(rule) }}</td>
                <td>{{ rule.priority }}</td>
                <td>
                    <span :class="rule.active ? 'uc-active-yes' : 'uc-active-no'">
                        {{ rule.active ? '✓' : '✗' }}
                    </span>
                </td>
                <td class="uc-free-product-manager__actions">
                    <sw-button v-if="acl.can('sellops_ultimate_cart.editor')" size="x-small" @click="openEdit(rule)">
                        {{ $tc('sellops-ultimate-cart.freeProducts.editButton') }}
                    </sw-button>
                    <sw-button v-if="acl.can('sellops_ultimate_cart.deleter')" size="x-small" variant="danger" @click="confirmDelete(rule.id)">
                        {{ $tc('sellops-ultimate-cart.freeProducts.deleteButton') }}
                    </sw-button>
                </td>
            </tr>
        </tbody>
    </table>
    {% endblock %}

    {% block uc_free_product_manager_modal %}
    {#
      Two-phase close (see _closeFormModal):
      1) v-if on the form body tears down selects first — their result lists
         teleport to <body> and must die before the modal itself.
      2) v-if on the modal then removes the dialog.
      Closing both in one flush, or using v-show, either crashes Vue
      (insertBefore) or leaves orphaned sales-channel dropdowns.
    #}
    <sw-modal
        ref="formModal"
        v-if="showModal"
        :title="isNewRule
            ? $tc('sellops-ultimate-cart.freeProducts.modalTitleNew')
            : $tc('sellops-ultimate-cart.freeProducts.modalTitleEdit')"
        @modal-close="cancelModal"
    >
        <div v-if="showFormBody" class="uc-free-product-manager__form">

            {# Row 1: active + once per customer #}
            <div class="uc-free-product-manager__form-row">
                <sw-switch-field
                    :value="form.active"
                    :label="$tc('sellops-ultimate-cart.freeProducts.fieldActive')"
                    bordered
                    @update:value="form.active = $event"
                />
                <sw-switch-field
                    :value="form.oncePerCustomer"
                    :label="$tc('sellops-ultimate-cart.freeProducts.fieldOncePerCustomer')"
                    bordered
                    @update:value="form.oncePerCustomer = $event"
                />
            </div>
            <p class="uc-free-product-manager__hint">
                {{ $tc('sellops-ultimate-cart.freeProducts.fieldOncePerCustomerHelp') }}
            </p>

            {# Row 2: validity window #}
            <div class="uc-free-product-manager__form-row">
                <sw-datepicker
                    date-type="datetime"
                    :value="form.activeFrom"
                    :label="$tc('sellops-ultimate-cart.freeProducts.fieldActiveFrom')"
                    @update:value="form.activeFrom = $event"
                />
                <sw-datepicker
                    date-type="datetime"
                    :value="form.activeUntil"
                    :label="$tc('sellops-ultimate-cart.freeProducts.fieldActiveUntil')"
                    @update:value="form.activeUntil = $event"
                />
            </div>

            {# Sales channels — sw-entity-multi-id-select uses value / update:value (not ids) #}
            <sw-entity-multi-id-select
                :repository="salesChannelRepository"
                :value="form.salesChannelIds"
                :label="$tc('sellops-ultimate-cart.freeProducts.fieldSalesChannels')"
                @update:value="form.salesChannelIds = $event"
            />
            <p class="uc-free-product-manager__hint">
                {{ $tc('sellops-ultimate-cart.freeProducts.fieldSalesChannelsHelp') }}
            </p>

            {# Free product #}
            <sw-entity-single-select
                ref="productSelect"
                entity="product"
                :criteria="productCriteria"
                :value="form.productId"
                :label="$tc('sellops-ultimate-cart.freeProducts.fieldProduct')"
                :placeholder="$tc('sellops-ultimate-cart.freeProducts.fieldProductPlaceholder')"
                @update:value="form.productId = $event"
            />

            {# Row 3: threshold + priority #}
            <div class="uc-free-product-manager__form-row">
                <sw-number-field
                    :value="form.cartValue"
                    :label="$tc('sellops-ultimate-cart.freeProducts.fieldCartValue')"
                    :min="0"
                    :step="0.01"
                    @change="form.cartValue = $event"
                />
                <sw-number-field
                    :value="form.priority"
                    :label="$tc('sellops-ultimate-cart.freeProducts.fieldPriority')"
                    :step="1"
                    number-type="int"
                    @change="form.priority = $event"
                />
            </div>

            {# Gift group — rules sharing the same group id become one "pick
               up to N of these" set on the storefront instead of each being
               independently offered/claimed. Joining an EXISTING group used
               to mean retyping its name by hand into a free-text field —
               easy to mistype (a single typo silently created a second,
               separate group instead of joining the first). Now a dropdown
               of every group name already in use, plus a "+ Create new
               group" option that swaps in the free-text field for typing a
               brand new name — see isCreatingNewGroup / onGroupSelectChange
               / chooseExistingGroup in index.js. #}
            <div class="uc-free-product-manager__form-row">
                <template v-if="existingGroupNames.length && !isCreatingNewGroup">
                    <sw-single-select
                        ref="groupSelect"
                        :value="form.groupId"
                        :label="$tc('sellops-ultimate-cart.freeProducts.fieldGroup')"
                        :placeholder="$tc('sellops-ultimate-cart.freeProducts.fieldGroupSelectPlaceholder')"
                        :help-text="$tc('sellops-ultimate-cart.freeProducts.fieldGroupHelp')"
                        :options="groupSelectOptions"
                        @update:value="onGroupSelectChange"
                    />
                </template>
                <template v-else>
                    <div class="uc-free-product-manager__group-text">
                        <sw-text-field
                            :value="form.groupId"
                            :label="$tc('sellops-ultimate-cart.freeProducts.fieldGroup')"
                            :placeholder="$tc('sellops-ultimate-cart.freeProducts.fieldGroupPlaceholder')"
                            :help-text="$tc('sellops-ultimate-cart.freeProducts.fieldGroupHelp')"
                            @update:value="form.groupId = $event"
                        />
                        <a
                            v-if="existingGroupNames.length"
                            href="#"
                            class="uc-free-product-manager__link"
                            @click.prevent="chooseExistingGroup"
                        >
                            {{ $tc('sellops-ultimate-cart.freeProducts.fieldGroupChooseExisting') }}
                        </a>
                    </div>
                </template>
                <sw-number-field
                    v-if="form.groupId"
                    :value="form.maxSelectable"
                    :label="$tc('sellops-ultimate-cart.freeProducts.fieldMaxSelectable')"
                    :help-text="$tc('sellops-ultimate-cart.freeProducts.fieldMaxSelectableHelp')"
                    :min="1"
                    :step="1"
                    number-type="int"
                    @change="form.maxSelectable = $event"
                />
            </div>
            <p v-if="form.groupId && groupCounts[form.groupId]" class="uc-free-product-manager__hint">
                {{ $tc('sellops-ultimate-cart.freeProducts.fieldGroupSyncHint') }}
            </p>

            {# Icon — same media-library picker as the milestone progress bar's
               icon field (uc-milestone-manager). Stored value is a media
               UUID; ProgressBarResolver resolves it to a real URL when this
               gift rule is folded into the progress bar alongside milestones
               (see FreeProductGiftResolver::toProgressMilestones()). #}
            <sellops-uc-media-upload
                :value="form.iconUrl"
                :default-preview-url="freeProductDefaultIconUrl"
                :label="$tc('sellops-ultimate-cart.freeProducts.fieldIcon')"
                :help-text="$tc('sellops-ultimate-cart.freeProducts.fieldIconHelp')"
                @update:value="form.iconUrl = $event"
            />

            {# Labels — shown on the progress bar for this gift. Leave empty
               to use the plugin's default wording. #}
            <sw-text-field
                :value="form.labelBefore"
                :label="$tc('sellops-ultimate-cart.freeProducts.fieldLabelBefore')"
                :placeholder="$tc('sellops-ultimate-cart.freeProducts.fieldLabelBeforePlaceholder', 0, { token: '{remaining}' })"
                :help-text="$tc('sellops-ultimate-cart.freeProducts.fieldLabelBeforeHelp', 0, { token: '{remaining}' })"
                @update:value="form.labelBefore = $event"
            />
            <sw-text-field
                :value="form.labelAfter"
                :label="$tc('sellops-ultimate-cart.freeProducts.fieldLabelAfter')"
                :placeholder="$tc('sellops-ultimate-cart.freeProducts.fieldLabelAfterPlaceholder')"
                @update:value="form.labelAfter = $event"
            />

            {# Celebration animation — plays once, client-side, the moment a
               shopper unlocks this gift. Purely cosmetic; doesn't affect the
               reward itself. See docs/celebration-animations-architecture.md. #}
            <sw-single-select
                ref="animationTypeSelect"
                :value="form.animationType"
                :label="$tc('sellops-ultimate-cart.freeProducts.fieldAnimationType')"
                :help-text="$tc('sellops-ultimate-cart.freeProducts.fieldAnimationTypeHelp')"
                :options="animationTypeOptions"
                @update:value="form.animationType = $event"
            />

        </div>

        <template #modal-footer>
            <sw-button size="small" @click="cancelModal">
                {{ $tc('global.default.cancel') }}
            </sw-button>
            <sw-button v-if="acl.can('sellops_ultimate_cart.editor')" variant="primary" size="small" :isLoading="isSaving" @click="saveRule">
                {{ $tc('global.default.save') }}
            </sw-button>
        </template>
    </sw-modal>
    {% endblock %}

    {% block uc_free_product_manager_delete %}
    <sw-modal
        v-if="showDeleteConfirm"
        :title="$tc('sellops-ultimate-cart.freeProducts.deleteConfirmTitle')"
        variant="small"
        @modal-close="cancelDelete"
    >
        <p>{{ $tc('sellops-ultimate-cart.freeProducts.deleteConfirmText') }}</p>
        <template #modal-footer>
            <sw-button size="small" @click="cancelDelete">
                {{ $tc('global.default.cancel') }}
            </sw-button>
            <sw-button v-if="acl.can('sellops_ultimate_cart.deleter')" variant="danger" size="small" :isLoading="isDeleting" @click="executeDelete">
                {{ $tc('global.default.delete') }}
            </sw-button>
        </template>
    </sw-modal>
    {% endblock %}

</div>
{% endblock %}
